import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { fetchEarnings, requestWithdrawal, previewFee } from "@/lib/api";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DollarSign, TrendingUp, Wallet, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function EarningsPage() {
  const { user, loading, refresh } = useAuth();
  const { format, code: curCode } = useCurrency();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [wdOpen, setWdOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [destination, setDestination] = useState("");

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  const load = () => fetchEarnings().then(setData).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]);

  if (!data) return <div className="p-16 text-center">Loading…</div>;

  const profileComplete = !!user?.profile_complete;
  const amt = Number(amount) || 0;
  const fee = Math.round(amt * 0.20 * 100) / 100;
  const net = Math.round((amt - fee) * 100) / 100;
  const invalid = !amt || amt <= 0 || amt > data.available;

  const submitWithdraw = async () => {
    if (invalid) { toast.error("Enter a valid amount"); return; }
    try {
      await requestWithdrawal({ amount: amt, method: "bank", destination });
      toast.success(`Withdrawal requested. You'll receive ${format(net)} after 20% platform fee.`);
      setWdOpen(false);
      setAmount("");
      setDestination("");
      load();
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
      <div className="overline text-primary mb-2">◆ SELLER EARNINGS</div>
      <h1 className="text-4xl font-bold tracking-tight mb-8">Your ledger</h1>

      <div className="grid md:grid-cols-4 gap-4 mb-10">
        <Stat testid="earn-total" icon={<TrendingUp className="w-5 h-5"/>} label="Total earned" value={format(data.total_earned)}/>
        <Stat testid="earn-available" icon={<Wallet className="w-5 h-5"/>} label="Available" value={format(data.available)} highlight/>
        <Stat testid="earn-pending" icon={<Clock className="w-5 h-5"/>} label="Pending (active orders)" value={format(data.pending)}/>
        <Stat testid="earn-withdrawn" icon={<DollarSign className="w-5 h-5"/>} label="Total withdrawn" value={format(data.total_withdrawn)}/>
      </div>

      {!profileComplete && (
        <div data-testid="profile-incomplete-banner" className="border border-destructive bg-destructive/5 p-5 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5"/>
          <div className="flex-1">
            <div className="font-semibold mb-1">Complete your seller profile to withdraw</div>
            <div className="text-sm text-muted-foreground">Add mobile, address, bank details and an ID document — we need these to pay you out.</div>
          </div>
          <Link to="/profile" data-testid="complete-profile-cta" className="bg-primary text-primary-foreground px-4 py-2 mono text-xs hover:bg-primary/90 whitespace-nowrap">COMPLETE PROFILE →</Link>
        </div>
      )}

      <div className="flex justify-end mb-6">
        <Dialog open={wdOpen} onOpenChange={setWdOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="request-withdraw-btn"
              disabled={data.available <= 0 || !profileComplete}
              className="bg-primary text-primary-foreground px-5 py-2.5 mono text-xs hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
            >REQUEST WITHDRAWAL</button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle>Withdraw earnings</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">Available: <b className="text-foreground mono">{format(data.available)}</b> <span className="opacity-60 text-xs">(${data.available.toFixed(2)} USD)</span></div>
              <div>
                <label className="overline block mb-2">Amount (USD)</label>
                <Input data-testid="withdraw-amount" type="number" min="1" max={data.available} value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-none border-foreground/20 mono" placeholder="0.00"/>
              </div>
              <div>
                <label className="overline block mb-2">Notes to admin (optional)</label>
                <Input data-testid="withdraw-destination" value={destination} onChange={(e) => setDestination(e.target.value)} className="rounded-none border-foreground/20" placeholder="Anything the admin should know…"/>
              </div>

              {/* Fee breakdown */}
              <div data-testid="withdraw-fee-preview" className="border border-foreground/20 bg-muted/40 p-4 text-sm space-y-2">
                <div className="flex justify-between"><span className="text-muted-foreground">Requested</span><span className="mono">{format(amt)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Platform fee (20%)</span><span className="mono text-destructive">− {format(fee)}</span></div>
                <div className="flex justify-between border-t border-foreground/15 pt-2 font-semibold"><span>You receive</span><span data-testid="withdraw-net-usd" className="mono">{format(net)}</span></div>
                {curCode !== "USD" && <div className="flex justify-between text-xs opacity-60"><span>USD equivalent</span><span className="mono">${net.toFixed(2)}</span></div>}
              </div>
              <p className="text-xs text-muted-foreground">Funds are sent to the bank account in your Seller Profile.</p>
            </div>
            <DialogFooter>
              <button data-testid="withdraw-submit-btn" disabled={invalid} onClick={submitWithdraw} className="bg-primary text-primary-foreground px-6 py-2.5 mono text-xs hover:bg-primary/90 disabled:opacity-40">REQUEST WITHDRAWAL</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Withdrawal history */}
      <Section title="◆ Withdrawal history">
        {data.withdrawals.length === 0 ? (
          <div className="border border-dashed border-foreground/20 p-8 text-center text-sm text-muted-foreground">No withdrawals yet.</div>
        ) : (
          <div className="border border-foreground/15 overflow-x-auto">
            <div className="grid grid-cols-[1fr_90px_90px_90px_140px_110px] gap-4 px-5 py-3 border-b border-foreground/15 bg-muted overline min-w-[820px]">
              <div>◆ ID</div><div>Gross</div><div>Fee</div><div>Net</div><div>UTR / Ref</div><div>Status</div>
            </div>
            {data.withdrawals.map((w, i) => (
              <div key={w.withdrawal_id} data-testid={`wd-row-${i}`} className="grid grid-cols-[1fr_90px_90px_90px_140px_110px] gap-4 px-5 py-4 border-b border-foreground/10 items-center text-sm min-w-[820px]">
                <div className="mono text-xs truncate">{w.withdrawal_id}</div>
                <div className="mono">{format(w.gross_amount ?? w.amount ?? 0)}</div>
                <div className="mono text-destructive">− {format(w.platform_fee ?? 0)}</div>
                <div className="mono font-semibold">{format(w.net_amount ?? w.amount ?? 0)}</div>
                <div data-testid={`wd-utr-${i}`} className="mono text-[11px] truncate" title={w.utr_number || ""}>
                  {w.utr_number || <span className="opacity-40">—</span>}
                </div>
                <div>
                  <span className={`text-xs mono uppercase px-2 py-1 border ${w.status === "paid" ? "border-primary text-primary bg-primary/10" : "border-foreground/40"}`}>
                    {w.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="◆ Completed orders">
        {data.completed_orders.length === 0 ? (
          <div className="border border-dashed border-foreground/20 p-8 text-center text-sm text-muted-foreground">No completed orders yet.</div>
        ) : (
          <div className="border border-foreground/15">
            <div className="grid grid-cols-[1fr_120px_140px] gap-4 px-5 py-3 border-b border-foreground/15 bg-muted overline">
              <div>◆ Gig</div><div>Buyer</div><div>Earnings</div>
            </div>
            {data.completed_orders.map((o, i) => (
              <div key={o.order_id} className="grid grid-cols-[1fr_120px_140px] gap-4 px-5 py-4 border-b border-foreground/10 items-center text-sm">
                <Link to={`/order/${o.order_id}`} className="font-medium hover:text-primary line-clamp-1">{o.gig_title}</Link>
                <div className="text-muted-foreground">{o.buyer_name}</div>
                <div className="mono font-medium">{format(o.package_price)}</div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function Stat({ icon, label, value, highlight, testid }) {
  return (
    <div data-testid={testid} className={`border p-5 ${highlight ? "border-primary bg-primary text-primary-foreground" : "border-foreground/15 bg-card"}`}>
      <div className={`flex items-center justify-between mb-4 ${highlight ? "opacity-80" : "text-muted-foreground"}`}>{icon}</div>
      <div className="mono text-3xl font-bold">{value}</div>
      <div className={`overline mt-1 ${highlight ? "opacity-80" : ""}`}>{label}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-10">
      <div className="overline mb-3">{title}</div>
      {children}
    </div>
  );
}
